<template>
  <content-container :table-row-count="tableData.length">
    <!-- 搜索条件区域 -->
    <search-container>
      <search-col>
        <el-input v-model="params.customerNo" placeholder="自由职业者编号" clearable @keydown="onEnter" />
      </search-col>
      <search-col>
        <el-input v-model="params.name" placeholder="自由职业者名称" clearable @keydown="onEnter" />
      </search-col>
      <search-col>
        <el-input v-model="params.idNo" placeholder="身份证号" clearable @keydown="onEnter" />
      </search-col>
      <search-col>
        <el-input v-model="params.contactPhone" placeholder="通讯手机号" clearable @keydown="onEnter" />
      </search-col>
      <search-col v-if="from !== 'server'">
        <el-select
          v-model="params.dutyType"
          filterable
          placeholder="业务线"
          clearable
          @keyup.enter="doSearch"
        >
          <el-option :label="freelancerDutyTypeLabel[EFreelancerDutyType.CROWD]" :value="EFreelancerDutyType.CROWD" />
          <el-option :label="freelancerDutyTypeLabel[EFreelancerDutyType.MATCH]" :value="EFreelancerDutyType.MATCH" />
        </el-select>
      </search-col>
      <search-col>
        <el-input v-model="params.serverName" placeholder="合作服务商" clearable @keydown="onEnter" />
      </search-col>
      <search-col v-if="from !== 'server' && from !== 'agent'">
        <el-input v-model="params.topAgentName" placeholder="所属特代" clearable @keydown="onEnter" />
      </search-col>
      <search-col>
        <el-input v-model="params.companyName" placeholder="所属企业" clearable @keydown="onEnter" />
      </search-col>

      <template #buttons>
        <el-button @click="resetForm">
          <svg-icon icon="tabler:refresh" />重置
        </el-button>
        <el-button type="primary" @click="doSearch">
          <svg-icon icon="lucide:search" />查询
        </el-button>
      </template>
    </search-container>

    <!-- 非搜索区域的额外按钮 -->
    <div>
      <el-button type="primary" class="mb-[20px]" @click="handleExportTable">
        <svg-icon icon="mingcute:file-export-line" />导出
      </el-button>
    </div>

    <!-- 表格数据 -->
    <el-table 
      ref="freelancerTable" 
      v-loading="loading" 
      class="normal-table" 
      :border="true" 
      :scrollbar-always-on="true" 
      :data="tableData" 
      :cell-style="initOperatorColumnStyle"
      @selection-change="handleSelectionChange"
    >
      <el-table-column align="center" type="selection" :min-width="55" />
      <el-table-column align="left" prop="name" label="自由职业者名称" :width="130" show-overflow-tooltip />
      <el-table-column align="left" prop="idNo" label="身份证号" :min-width="120" show-overflow-tooltip />
      <el-table-column align="left" prop="bankCard" label="银行卡号" :min-width="130" show-overflow-tooltip>
        <template #default="scope">
          {{ scope.row.bankCard?.toString() }}
        </template>
      </el-table-column>
      <el-table-column align="left" prop="contactPhones" label="通信手机号" :min-width="120" show-overflow-tooltip>
        <template #default="scope">
          {{ scope.row.contactPhones?.toString() }}
        </template>
      </el-table-column>
      <el-table-column align="left" prop="alipayAccount" label="支付宝号" :min-width="120" show-overflow-tooltip>
        <template #default="scope">
          {{ scope.row.alipayAccount?.toString() }}
        </template>
      </el-table-column>
      <el-table-column align="left" prop="dutyType" label="业务线" :min-width="120">
        <template #default="scope">
          {{ scope.row.dutyTypeStr || '-' }}
        </template>
      </el-table-column>
      <el-table-column align="left" prop="serverNames" label="合作服务商" :min-width="120" show-overflow-tooltip>
        <template #default="scope">
          {{ scope.row.serverNames?.toString() }}
        </template>
      </el-table-column>
      <el-table-column v-if="from !== 'server' && from !== 'agent'" align="left" prop="topAgentNames" label="所属特代" :min-width="120" show-overflow-tooltip>
        <template #default="scope">
          {{ scope.row.topAgentNames?.toString() }}
        </template>
      </el-table-column>
      <el-table-column align="left" prop="companyNames" label="所属企业" :min-width="120" show-overflow-tooltip>
        <template #default="scope">
          {{ scope.row.companyNames?.toString() }}
        </template>
      </el-table-column>
      <el-table-column align="left" prop="customerNo" label="自由职业者编号" :min-width="130" show-overflow-tooltip />

      <el-table-column
        v-if="buttons.length > 0"
        align="center"
        prop="actions"
        fixed="right"
        label="操作"
      >
        <template #default="scope">
          <table-row-buttons :buttons="buttons" :row="scope.row" />
        </template>
      </el-table-column>
    </el-table>

    <!-- 底部分页 -->
    <template #footer>
      <el-pagination
        v-model:current-page="params.page"
        v-model:page-size="params.size"
        :page-sizes="[defaultPageSize, 25, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        background
        @size-change="onSizeChange"
        @current-change="onCurrentPageChange"
      />
    </template>
  </content-container>
</template>

<script setup lang="ts">
import { reactive, ref, PropType } from 'vue';
import { ElMessage } from 'element-plus';
import { initOperatorColumnStyle, SvgIcon, TableRowButtons } from '@vue3/components';
import {
  ContentContainer,
  SearchContainer,
  SearchCol,
  useTblPermissionButtons,
  queryFreelancerPage,
  exportFreelancerPage,
} from '@core/main';
import { EAuthStatus, IFreelancerItem, IFreelancerListParams, freelancerDutyTypeLabel, EFreelancerDutyType } from '@core/main/types';
import { useRouter } from 'vue-router';
import { downloadFile } from '@core/tools';

defineOptions({ name: 'UnRegisteredFreelancer' });

defineProps({
  from: {
    type: String as PropType<'saas' | 'company' | 'agent' | 'server'>,
    default: 'saas',
  },
});

const defaultPageSize = 12;
const router = useRouter();
const params = reactive<IFreelancerListParams>({
  page: 1,
  size: defaultPageSize,
});
const loading = ref(false);
const total = ref(0);
const tableData = ref<IFreelancerItem[]>([]);
const selectFreelancers = ref([]);
const freelancerTable = ref();
  
const { buttons } = useTblPermissionButtons([
  {
    name: '详情',
    code: '',
    action: doDetail,
  },
]);

async function fetchData() {
  loading.value = true;

  try {
    const { data, total: all } = await queryFreelancerPage({
      ...params,
      authStatuses: `${EAuthStatus.No},${EAuthStatus.Ing}`, 
    });
    
    tableData.value = data;
    total.value = all;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  loading.value = false;
}

function resetForm() {
  for (const key in params) {
    if (!['page', 'size'].includes(key)) {
      params[key] = undefined;
    }
  }
}

function onSizeChange(size: number) {
  params.size = size;
  params.page = 1;
  fetchData();
}

function onCurrentPageChange(page: number) {
  params.page = page;
  fetchData();
}

function doSearch() {
  params.page = 1;
  fetchData();
}

function onEnter(e) {
  if (e.keyCode === 13) doSearch();
}

function doDetail(row: any) {
  router.push({
    name: 'FreelancerDetail',
    query: {
      idNo: row.idNo,
      authStatus: row.authStatus, 
      serverId: row.serverIds,
    },
  });
}

function handleSelectionChange(val: any) {
  selectFreelancers.value = val.map((v) => v.idNo);
}

async function handleExportTable() {
  try {
    const res = await exportFreelancerPage({
      ...params,
      authStatuses: `${EAuthStatus.No},${EAuthStatus.Ing}`, 
    });

    downloadFile(res, '', 'xlsx');
    ElMessage.success('导出成功');
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

fetchData();
</script>
